/**
 * Phase 7-lite — Push Health diagnostic page.
 *
 * Shows every SA + dispatcher subscription with last_seen + endpoint gateway
 * (apple/google/mozilla). Lets the founder send a diagnostic push to their
 * own device to verify delivery end-to-end without placing a test order.
 *
 * Use when push notifications stop arriving: if `subscription_count > 0` and
 * the test push returns `sent=1`, the gateway accepted it → issue is on the
 * device (iOS throttling, OS notif settings, PWA not installed). If `sent=0`
 * or `removed=1`, the subscription is dead → user must re-toggle the bell.
 */
import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate, Link } from 'react-router-dom';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { ArrowLeft, RefreshCw, Bell, Send, CheckCircle2, XCircle, Trash2, Eye } from 'lucide-react';
import { Navbar } from '../../components/Navbar';

const API_URL = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/+$/, '');

const fmtTime = (iso) => {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    const diff = (Date.now() - d.getTime()) / 1000;
    if (diff < 60) return `${Math.floor(diff)}s ago`;
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    return `${Math.floor(diff / 86400)}d ago`;
  } catch { return iso; }
};

const gatewayColor = (g) => ({
  apple:   'text-[#A1A1A6] border-[#A1A1A6]/40 bg-[#A1A1A6]/10',
  google:  'text-[#4285F4] border-[#4285F4]/40 bg-[#4285F4]/10',
  mozilla: 'text-[#FF7139] border-[#FF7139]/40 bg-[#FF7139]/10',
}[g] || 'text-[#39FF14] border-[#39FF14]/40 bg-[#39FF14]/10');

export default function PushHealthPage() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [testing, setTesting] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [testingCheckout, setTestingCheckout] = useState(false);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const { data: d } = await axios.get(`${API_URL}/api/admin/push-health`, { withCredentials: true });
      setData(d);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load push health');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const sendTestPush = async () => {
    setTesting(true);
    try {
      const { data: d } = await axios.post(`${API_URL}/api/admin/push-health/test`, {}, { withCredentials: true });
      const s = d.stats || {};
      if (s.sent > 0) {
        toast.success(`Push accepted by gateway · sent=${s.sent}`, {
          description: 'Check your device — if no notification appeared, your OS is throttling/silencing.',
          duration: 10000,
        });
      } else if (s.removed > 0) {
        toast.error(`Subscription is dead · removed=${s.removed}`, {
          description: 'Re-toggle the bell icon to resubscribe.',
          duration: 10000,
        });
      } else {
        toast.warning(`No subscription · sent=0`, {
          description: 'Click the bell icon to subscribe first.',
          duration: 10000,
        });
      }
      // Refresh to update last_seen
      setTimeout(fetchAll, 1500);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Test push failed');
    } finally {
      setTesting(false);
    }
  };

  const verifyMine = async () => {
    setVerifying(true);
    try {
      const { data: d } = await axios.post(`${API_URL}/api/admin/push-health/verify-mine`, {}, { withCredentials: true });
      if (d.tested === 0) {
        toast.warning('No active subscriptions on your account to verify.', { duration: 8000 });
      } else if (d.dead > 0) {
        toast.success(`Cleaned ${d.dead} dead endpoint(s) · ${d.alive} alive`, {
          description: 'Dead = PWA deleted or notif permission revoked. Re-toggle the bell to subscribe again.',
          duration: 10000,
        });
      } else {
        toast.success(`All ${d.alive} of your subscription(s) are alive`, {
          description: 'Check your device — if no notification appeared, your OS is throttling/silencing.',
          duration: 10000,
        });
      }
      setTimeout(fetchAll, 1500);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Verify failed');
    } finally {
      setVerifying(false);
    }
  };

  const sendTestCheckoutAlert = async () => {
    setTestingCheckout(true);
    try {
      const { data: d } = await axios.post(
        `${API_URL}/api/admin/push-health/test-checkout-alert`,
        {},
        { withCredentials: true },
      );
      toast.success('Test checkout-failure alert fired · check your device + inbox', {
        description: (d.your_active_subs_before > 0
          ? `Push targeted ${d.your_active_subs_before} subscription(s) for your account, and email was sent to you. If no push lands within 10s, check Settings → Notifications → Safari/PWA.`
          : 'You have ZERO active push subscriptions — only the email will arrive. Click the bell icon below to subscribe first, then retry.'),
        duration: 14000,
      });
      setTimeout(fetchAll, 2000);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Test checkout alert failed');
    } finally {
      setTestingCheckout(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#050003] text-white dispatch-safe-top" data-testid="push-health-page">
      <Navbar />
      <div className="max-w-6xl mx-auto px-4 py-6">
        <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              onClick={() => navigate('/dispatch')}
              className="text-[#39FF14] hover:bg-[#39FF14]/10 rounded-none"
              data-testid="back-to-dispatch"
            >
              <ArrowLeft className="w-4 h-4 mr-1" /> BACK
            </Button>
            <div>
              <h1 className="text-[#39FF14] font-mono text-2xl font-bold flex items-center gap-2">
                <Bell className="w-6 h-6" />
                PUSH_HEALTH
              </h1>
              <p className="text-[#4B6B52] font-mono text-sm">
                {'// Per-user push subscription audit. VAPID='}
                {data?.vapid_configured ? (
                  <span className="text-[#39FF14]">CONFIGURED</span>
                ) : (
                  <span className="text-[#FF3B30]">MISSING</span>
                )}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <Button
              onClick={sendTestPush}
              disabled={testing}
              className="bg-[#FF9F0A] text-[#050003] hover:bg-[#FFB341] rounded-none font-mono"
              data-testid="send-test-push"
            >
              <Send className={`w-4 h-4 mr-2 ${testing ? 'animate-pulse' : ''}`} />
              SEND TEST PUSH TO ME
            </Button>
            <Button
              onClick={verifyMine}
              disabled={verifying}
              className="bg-[#FF3B30] text-white hover:bg-[#FF5C52] rounded-none font-mono"
              data-testid="verify-mine"
              title="Pings each of your endpoints and removes any dead ones (e.g. from a deleted PWA)."
            >
              <Trash2 className={`w-4 h-4 mr-2 ${verifying ? 'animate-pulse' : ''}`} />
              VERIFY &amp; CLEAN MY SUBS
            </Button>
            <Button
              onClick={sendTestCheckoutAlert}
              disabled={testingCheckout}
              className="bg-[#FF6B6B] text-[#050003] hover:bg-[#FF8585] rounded-none font-mono"
              data-testid="test-checkout-alert"
              title="Fires the full cart-failure alert pipeline (push + email + throttle + badge) targeting only you. Use this to verify you receive the same alert customers' checkout failures trigger."
            >
              <Send className={`w-4 h-4 mr-2 ${testingCheckout ? 'animate-pulse' : ''}`} />
              TEST CART FAILURE ALERT
            </Button>
            <Button
              onClick={fetchAll}
              disabled={loading}
              variant="outline"
              className="border-[#39FF14] text-[#39FF14] hover:bg-[#39FF14]/10 rounded-none font-mono"
              data-testid="refresh-push-health"
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              REFRESH
            </Button>
          </div>
        </div>

        {/* Operator demo link — preview the install flow inside the installed PWA */}
        <div className="mb-4 p-3 border border-[#39FF14]/20 bg-[#0C0612] flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-start gap-2 flex-1 min-w-0">
            <Eye className="w-4 h-4 text-[#39FF14] mt-0.5 shrink-0" />
            <div className="font-mono text-xs text-[#A3C7A8]">
              <div className="text-[#39FF14] font-bold mb-0.5">PREVIEW INSTALL FLOW</div>
              <div className="leading-relaxed">
                Open the URL below on any device to see the bottom pill + 3-slide walkthrough — even inside the installed PWA. Useful for demoing the install flow to less-technical customers.
              </div>
            </div>
          </div>
          <a
            href="/?preview_install=1"
            target="_blank"
            rel="noopener noreferrer"
            className="bg-[#39FF14] text-[#050003] hover:bg-[#4AF626] rounded-none font-mono text-xs font-bold px-3 py-2 inline-flex items-center gap-1 shrink-0"
            data-testid="preview-install-link"
          >
            <Eye className="w-3.5 h-3.5" /> OPEN PREVIEW ↗
          </a>
        </div>

        {/* User cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {(data?.users || []).map((u) => (
            <div
              key={u.user_id}
              className="border border-[#39FF14]/20 bg-[#0C0612] p-4"
              data-testid={`push-health-${u.email}`}
            >
              <div className="flex items-start justify-between mb-2 gap-2">
                <div>
                  <Link
                    to={`/dispatch/admin/users?openUser=${u.user_id}&from=push-health`}
                    className="font-mono text-sm text-white hover:text-[#39FF14] hover:underline"
                    data-testid={`push-health-customer-link-${u.user_id}`}
                  >
                    {u.email}
                  </Link>
                  <div className="font-mono text-xs text-[#4B6B52]">
                    {u.name || '—'} · {u.role}{u.region ? ` · ${u.region.toUpperCase()}` : ''}
                  </div>
                </div>
                <div className="flex items-center gap-2 font-mono text-xs">
                  {u.subscription_count > 0 ? (
                    <span className="flex items-center gap-1 text-[#39FF14]">
                      <CheckCircle2 className="w-3.5 h-3.5" /> {u.subscription_count} SUB{u.subscription_count !== 1 ? 'S' : ''}
                    </span>
                  ) : (
                    <span className="flex items-center gap-1 text-[#FF3B30]">
                      <XCircle className="w-3.5 h-3.5" /> NO SUBS
                    </span>
                  )}
                </div>
              </div>
              <div className="font-mono text-xs text-[#4B6B52] mb-2">
                EMAIL ALERTS: {u.email_alerts_opt_out ? (
                  <span className="text-[#FF3B30]">OPTED OUT</span>
                ) : (
                  <span className="text-[#39FF14]">ON</span>
                )}
              </div>
              {u.subscriptions.length === 0 ? (
                <div className="font-mono text-xs text-[#4B6B52] italic">
                  {'// no push subscriptions — user must click the bell icon'}
                </div>
              ) : (
                <div className="space-y-2">
                  {u.subscriptions.map((s, i) => (
                    <div key={i} className="border border-[#39FF14]/10 p-2">
                      <div className="flex items-center gap-2 mb-1">
                        <span className={`px-2 py-0.5 border font-mono text-[10px] uppercase ${gatewayColor(s.gateway)}`}>
                          {s.gateway}
                        </span>
                        {s.active ? (
                          <span className="font-mono text-[10px] text-[#39FF14]">ACTIVE</span>
                        ) : (
                          <span className="font-mono text-[10px] text-[#FF3B30]">INACTIVE</span>
                        )}
                        <span className="font-mono text-[10px] text-[#4B6B52] ml-auto">
                          seen {fmtTime(s.last_seen)}
                        </span>
                      </div>
                      <div className="font-mono text-[10px] text-[#A3C7A8] break-all leading-tight">
                        {s.ua_short || '—'}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>

        {(data?.users || []).length === 0 && !loading && (
          <div className="border border-[#39FF14]/20 bg-[#0C0612] p-8 text-center font-mono text-[#4B6B52]">
            {'// no users matched the filter'}
          </div>
        )}
      </div>
    </div>
  );
}
